import { PRODUCT, USER } from 'config/api-routes';
import { LikeTargetType } from 'types/like-target.types';
import { UpdateUserBankAccountType, UpdateUserEmailType, UserContactType } from 'types/user.types';
import { DELETE, GET, PATCH, POST } from 'utils/request';
import { formatResponse } from 'utils/service-helpers';

import { Language } from '../config';

export default class UserService {
  async updateUserProfileImage(data, opts = {}) {
    const response = await POST(USER.IMAGE.POST(), data, opts);

    return formatResponse(response);
  }

  async checkEmailValidation(opts = {}) {
    const response = await GET(USER.EMAIL.VALIDATE.GET(), opts);

    return formatResponse(response);
  }

  async updateUser(userData, opts = {}) {
    const response = await PATCH(USER.UPDATE.PATCH(), userData, opts);

    return formatResponse(response);
  }

  async updateUserContactDetails(userContactDetails: UserContactType, opts = {}) {
    const response = await PATCH(USER.UPDATE_CONTACT_DETAILS.PATCH(), userContactDetails, opts);

    return formatResponse(response);
  }

  async updateUserBankAccount(bankAccountDetails: UpdateUserBankAccountType, opts = {}) {
    const response = await PATCH(USER.UPDATE_BANK_ACCOUNT.PATCH(), bankAccountDetails, opts);

    return formatResponse(response);
  }

  async updateUserEmail(data: UpdateUserEmailType, opts = {}) {
    const response = await PATCH(USER.UPDATE_EMAIL.PATCH(), data, opts);

    return formatResponse(response);
  }

  async acceptTerms(opts = {}) {
    const response = await POST(USER.ACCEPT_TERMS.POST(), {}, opts);

    return formatResponse(response);
  }

  async getNotificationPreferences(opts = {}) {
    const response = await GET(USER.NOTIFICATION_PREFERENCES.GET(), opts);

    return formatResponse(response);
  }

  async updateNotificationPreference(data: { key: string; channel: string; value: boolean; source: string }, opts = {}) {
    const response = await PATCH(USER.NOTIFICATION_PREFERENCES.PATCH(), data, opts);

    return formatResponse(response);
  }

  async updateUserConsent(
    consentData: {
      consentAnalytics?: boolean;
      consentMarketing?: boolean;
      consentMarketingCommunications?: boolean;
      consentErrorTracking?: boolean;
      marketing_email_notifications_source?: string;
    },
    opts = {},
  ) {
    const response = await PATCH(USER.UPDATE_CONSENT.PATCH(), consentData, opts);

    return formatResponse(response);
  }

  async blockUser(targetUserId, opts = {}) {
    const response = await POST(USER.BLOCK(targetUserId), {}, opts);

    return formatResponse(response);
  }

  async unBlockUser(targetUserId, opts = {}) {
    const response = await DELETE(USER.BLOCK(targetUserId), {}, opts);

    return formatResponse(response);
  }

  async getCurrentUser(userId: number | '' = '', opts = {}) {
    const response = await GET(USER.ONE.GET(userId), opts);

    return formatResponse(response);
  }

  async getUserById(userId: number, opts = {}) {
    const response = await GET(USER.ONE.GET(userId), opts);

    return formatResponse(response);
  }

  async getUserProducts(shopId: number, opts = {}) {
    const response = await GET(
      PRODUCT.LIST.GET({
        shopId,
        status: ['published', 'reserved'],
        sortBy: 'createdAt',
        sortDir: 'desc',
      }),
      opts,
    );

    return formatResponse(response);
  }

  async getUserSoldProducts(shopId: number, opts = {}) {
    const response = await GET(
      PRODUCT.LIST.GET({
        shopId,
        status: 'sold',
        sortBy: 'createdAt',
        sortDir: 'desc',
      }),
      opts,
    );

    return formatResponse(response);
  }

  async likeUserShop(shopId: number, data = {}, opts = {}) {
    const response = await POST(USER.SHOP.LIKE.CREATE.POST(shopId), data, opts);

    return response?.data;
  }

  async likeUser(userId: number, opts = {}) {
    const response = await POST(USER.LIKE.CREATE.POST(userId), opts);

    return formatResponse(response);
  }

  async unlikeUser(userId: number, opts = {}) {
    const response = await DELETE(USER.LIKE.REMOVE.DELETE(userId), undefined, opts);

    return formatResponse(response);
  }

  async getLikedShops(queryParams?, opts = {}) {
    const response = await GET(USER.SHOP.LIKE.LIST.GET(queryParams), opts);

    return formatResponse(response);
  }

  async unlikeUserShop(shopId: number, opts = {}) {
    const response = await DELETE(USER.SHOP.LIKE.REMOVE.DELETE(shopId), undefined, opts);

    return formatResponse(response);
  }

  async getLikersList(type: LikeTargetType, userId: number, queryParams?, opts = {}) {
    const response = await GET(USER.LIKE.LIST.GET(type, userId, queryParams), opts);

    return formatResponse(response);
  }

  async getFeaturedUsers(queryParams?, opts = {}) {
    const response = await GET(USER.FEATURED.GET(queryParams), opts);

    return formatResponse(response);
  }

  async getUserContact(queryParams, opts = {}) {
    const response = await GET(USER.CONTACT.ONE.GET(queryParams), opts);

    return formatResponse(response);
  }

  async updateUserContact(userId: number, contact, opts = {}) {
    const response = await PATCH(USER.CONTACT.UPDATE.PATCH(userId), contact, opts);

    return formatResponse(response);
  }

  async withdrawMoney(data, opts = {}) {
    const response = await POST(USER.MONEY.WITHDRAW.POST(), data, opts);

    return formatResponse(response);
  }

  async getDeleteReasons(opts = {}) {
    const response = await GET(USER.DELETE.REASONS.GET(), opts);

    return formatResponse(response);
  }

  async deleteUser(deleteData, opts = {}) {
    const response = await DELETE(USER.DELETE.DELETE(), deleteData, opts);

    return formatResponse(response);
  }

  async checkVerifiedPhoneNumber(phoneNumber?: string, opts = {}) {
    const response = await GET(USER.PHONE.VERIFIED.GET(phoneNumber), opts);

    return formatResponse(response);
  }

  async updateUserLanguage(language: Language, opts = {}) {
    const response = await PATCH(USER.LANGUAGE.UPDATE.PATCH(), { language }, opts);

    return formatResponse(response);
  }
}
